import { useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatQrId, type IssueRobotQrResponse } from '@bhops/shared';
import { canEditMfg } from '@/permissions';
import { robotQrsApi } from '@/api/robot-qrs';
import { extractApiError } from '@/api/client';
import { kstDateTime } from '@/lib/format-date';
import { Button } from '@/components/Form';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useToast } from '@/components/Toast';
import { useAuthStore } from '@/stores/auth';
import { IssuedTokenModal } from './IssuedTokenModal';
import { Sheet, SheetEmpty, SheetHead, SheetTable } from './robot-detail-ui';

interface Props {
  robotId: string;
  robotSerial: string | null;
}

export function QRTab({ robotId, robotSerial }: Props) {
  const role = useAuthStore((s) => s.user?.role);
  // 계획서(2026-08-20) 요구1 ⓒ — 서버(ability.factory.ts 의 RobotQR create/update)
  // 는 MFG_MANAGER 를 이미 허용하는데 여기만 ADMIN/MANAGER 라 제조팀에게 버튼이
  // 숨어 있었다. canEditMfg 가 서버와 같은 집합(ADMIN·레거시 MANAGER·MFG_MANAGER).
  const canManage = role != null && canEditMfg(role);
  const qc = useQueryClient();
  const toast = useToast();
  const [issuedResult, setIssuedResult] = useState<IssueRobotQrResponse | null>(null);
  const [revokeConfirmOpen, setRevokeConfirmOpen] = useState(false);
  const [reissueConfirmOpen, setReissueConfirmOpen] = useState(false);

  const listQ = useQuery({
    queryKey: ['robot-qrs', robotId],
    queryFn: () => robotQrsApi.list(robotId),
  });

  const issue = useMutation({
    mutationFn: () => robotQrsApi.issue(robotId),
    onSuccess: (result) => {
      setIssuedResult(result);
      setReissueConfirmOpen(false);
      qc.invalidateQueries({ queryKey: ['robot-qrs', robotId] });
      qc.invalidateQueries({ queryKey: ['robot-timeline', robotId] });
      toast.success(`Rev ${result.qr.rev} QR을 발급했습니다.`, 'QR 발급');
    },
    onError: (err) => toast.error(extractApiError(err), '발급 실패'),
  });

  const revoke = useMutation({
    mutationFn: (qrId: string) => robotQrsApi.revoke(robotId, qrId, 'manual'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['robot-qrs', robotId] });
      qc.invalidateQueries({ queryKey: ['robot-timeline', robotId] });
      toast.success('QR이 폐기되었습니다.');
      setRevokeConfirmOpen(false);
    },
    onError: (err) => toast.error(extractApiError(err), '폐기 실패'),
  });

  const items = listQ.data?.items ?? [];
  const active = items.find((q) => q.active);

  return (
    <div className="rd-stack">
      {/* 2026-11-02 — 로봇 상세의 다른 탭과 같은 판(Sheet) 모양. */}
      <Sheet>
        <SheetHead
          title="현재 활성 QR"
          actions={
            <>
              {canManage && (
                <Button
                  type="button"
                  onClick={() => (active ? setReissueConfirmOpen(true) : issue.mutate())}
                  disabled={issue.isPending}
                >
                  {issue.isPending ? '발급 중…' : active ? '재발급' : '신규 발급'}
                </Button>
              )}
              {canManage && active && (
                <Button
                  type="button"
                  variant="secondary"
                  disabled={revoke.isPending}
                  onClick={() => setRevokeConfirmOpen(true)}
                >
                  폐기
                </Button>
              )}
            </>
          }
        />

        {listQ.isLoading ? (
          <SheetEmpty>불러오는 중…</SheetEmpty>
        ) : listQ.error ? (
          <SheetEmpty>
            <span style={{ color: 'var(--fg-danger)' }}>{extractApiError(listQ.error)}</span>
          </SheetEmpty>
        ) : active ? (
          <div style={{ padding: '0 24px 24px' }}>
            <div className="rd-version">
              {robotSerial ? formatQrId(robotSerial, active.rev) : `Rev ${active.rev}`}
            </div>
            <div className="kuun-caption" style={{ marginTop: 4, color: 'var(--fg-muted)' }}>
              {/* 추가수정 2차(26-07-27) §2 — 브라우저 로캘 대신 KST 고정. QR 발급/폐기는
                  보안 이력이라 시각까지 남기고, 대표 표시에는 기준 라벨을 붙인다. */}
              발급일: {kstDateTime(active.issuedAt, { label: true }) ?? '–'} · ID:{' '}
              <span className="kuun-mono">{active.id.slice(-8)}</span>
            </div>
            {/* batch-6 — Phase 7a 공개 트래킹 페이지 link.
                portal URL 환경변수 (VITE_PORTAL_BASE_URL) 가 없으면 상대 path 만. */}
            {robotSerial && <PublicTrackingLink serial={robotSerial} />}
          </div>
        ) : (
          <SheetEmpty>
            활성 QR이 없습니다. [신규 발급] 버튼을 눌러 첫 QR을 발급해 주세요.
          </SheetEmpty>
        )}
      </Sheet>

      <Sheet>
        <SheetHead title="전체 발급 이력" count={items.length} />
        {items.length === 0 ? (
          <SheetEmpty>발급 이력이 없습니다.</SheetEmpty>
        ) : (
          <SheetTable>
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>Rev</th>
                  <th style={th}>상태</th>
                  <th style={th}>발급일</th>
                  <th style={th}>폐기일</th>
                  <th style={th}>폐기 사유</th>
                </tr>
              </thead>
              <tbody>
                {items.map((q) => (
                  <tr key={q.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>
                      {robotSerial ? formatQrId(robotSerial, q.rev) : q.rev}
                    </td>
                    <td style={td}>
                      {q.active ? (
                        <span style={{ color: 'var(--robot-operating)' }}>활성</span>
                      ) : (
                        <span style={{ color: 'var(--fg-muted)' }}>폐기</span>
                      )}
                    </td>
                    <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>
                      {kstDateTime(q.issuedAt) ?? '–'}
                    </td>
                    <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>
                      {kstDateTime(q.revokedAt) ?? '–'}
                    </td>
                    <td style={td}>{q.revokedReason ?? '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SheetTable>
        )}
      </Sheet>

      <IssuedTokenModal
        open={Boolean(issuedResult)}
        onClose={() => setIssuedResult(null)}
        result={issuedResult}
        robotSerial={robotSerial ?? undefined}
      />

      <ConfirmDialog
        open={revokeConfirmOpen}
        title="QR 폐기 확인"
        message={active ? `Rev ${active.rev} QR을 폐기하시겠습니까?` : ''}
        confirmLabel="폐기"
        danger
        busy={revoke.isPending}
        onConfirm={() => {
          if (active) revoke.mutate(active.id);
        }}
        onCancel={() => setRevokeConfirmOpen(false)}
      />

      <ConfirmDialog
        open={reissueConfirmOpen}
        title="QR 재발급 확인"
        message={`지금 쓰는 Rev ${active?.rev} QR이 바로 폐기되고 새 QR이 발급됩니다. 이 작업은 되돌릴 수 없습니다.`}
        confirmLabel="재발급"
        danger
        busy={issue.isPending}
        onConfirm={() => issue.mutate()}
        onCancel={() => setReissueConfirmOpen(false)}
      />
    </div>
  );
}

// docs/work_log/v1025-remaining-work-batch-6 — Phase 7a 공개 트래킹 link.
// QR 발급 후 운영자가 "이 QR 이 어떤 페이지 로 연결되는가" 시각 확인.
// 실 QR 코드 이미지 생성 (qrcode lib) 은 follow-up.
function PublicTrackingLink({ serial }: { serial: string }) {
  // env 미설정 시 상대 path. 운영 배포 시 VITE_PORTAL_BASE_URL=https://r.bhops.com
  const portalBase =
    (import.meta as { env?: { VITE_PORTAL_BASE_URL?: string } }).env?.VITE_PORTAL_BASE_URL ?? '';
  const url = `${portalBase}/p/${encodeURIComponent(serial)}`;
  // batch-8 — backend QR endpoint (qrcode lib server-side). 외부 API 의존 제거.
  // `GET /robots/<sn>/qrs/png?size=200` (인증 불요 + 1h cache-control).
  // backend 가 portal `/p/<sn>` URL 을 QR PNG 으로 인코딩.
  const apiBase =
    (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL ?? '/api/v1';
  const qrSrc = `${apiBase}/robots/${encodeURIComponent(serial)}/qrs/png?size=200`;
  return (
    <div
      style={{
        marginTop: 10,
        padding: 12,
        background: 'var(--bg-sunken)',
        borderRadius: 'var(--r-card)',
        fontSize: 12,
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
      }}
    >
      <img
        src={qrSrc}
        width={120}
        height={120}
        alt={`QR ${serial}`}
        style={{
          border: '4px solid var(--fg-inverse)',
          borderRadius: 4,
          background: 'var(--fg-inverse)',
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: 'var(--fg-muted)', marginBottom: 4 }}>
          공개 트래킹 QR, 스캔하면 포털 페이지가 열립니다
        </div>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontFamily: 'var(--font-mono)',
            color: 'var(--fg-brand)',
            textDecoration: 'none',
            wordBreak: 'break-all',
            fontSize: 11,
          }}
          title="새 탭에서 열어 확인"
        >
          {url}
        </a>
        <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => window.open(qrSrc, '_blank', 'noopener,noreferrer')}
            style={qrActionBtn}
            title="QR 이미지를 새 탭에서 열어 내려받거나 인쇄합니다"
          >
            QR 이미지
          </button>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard?.writeText(url).catch(() => {});
            }}
            style={qrActionBtn}
            title="주소를 클립보드에 복사"
          >
            URL 복사
          </button>
        </div>
      </div>
    </div>
  );
}

const qrActionBtn: CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-btn)',
  padding: '4px 8px',
  fontSize: 11,
  color: 'var(--fg-default)',
  cursor: 'pointer',
};

const table: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th: CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  fontWeight: 600,
  color: 'var(--fg-muted)',
  background: 'var(--bg-sunken)',
};
const td: CSSProperties = { padding: '8px 10px', verticalAlign: 'middle' };
